import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { Package, ArrowLeft, ShoppingCart, Check, Sparkles, Loader2 } from "lucide-react";

export default function PaqueteDetalle() {
  const { slug } = useParams();
  const { cart, addBundle } = useCart();
  const [b, setB] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const inCart = !!(b && cart?.items?.some((i) => i.bundle_id === b.id));

  useEffect(() => {
    api.get(`/catalog/bundles/${slug}`)
      .then((r) => setB(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <PublicLayout><div className="max-w-3xl mx-auto px-4 py-24 text-gray-600">Paquete no encontrado.</div></PublicLayout>;
  if (!b) return <PublicLayout><div className="max-w-3xl mx-auto px-4 py-24 text-gray-600 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div></PublicLayout>;

  const handleAdd = async () => {
    if (inCart) return;
    try {
      await addBundle(b.id);
      toast.success("Paquete agregado al carrito.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <PublicLayout>
      <section className="max-w-6xl mx-auto px-4 py-12">
        <Link to="/catalogo/paquetes" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#0B0B0B] mb-4">
          <ArrowLeft className="w-3 h-3" /> Volver a paquetes
        </Link>

        <div className="grid lg:grid-cols-[1fr_360px] gap-8">
          <div>
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-[#F26A21] text-[#0B0B0B] uppercase font-bold tracking-widest">
              <Package className="w-3 h-3" /> Paquete
            </span>
            <h1 className="mt-3 text-4xl font-extrabold text-[#0B0B0B] font-display">{b.title}</h1>
            <p className="mt-2 text-lg text-gray-600">{b.description}</p>

            <h2 className="mt-8 text-lg font-bold text-[#0B0B0B]">Programas incluidos ({b.programs.length})</h2>
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              {b.programs.map((p) => (
                <Link key={p.id} to={`/catalogo/${p.slug}`}
                      data-testid={`bundle-program-${p.code}`}
                      className="p-4 rounded-2xl border border-gray-200 bg-white hover:border-[#F26A21] transition-colors">
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{p.code} · {p.format}</p>
                  <p className="mt-1 text-sm font-bold text-[#0B0B0B]">{p.title}</p>
                  <p className="mt-1 text-xs text-gray-600">{p.duration_text} · {p.modality}</p>
                  <p className="mt-2 text-xs text-gray-500 line-through">${(p.price_mxn || 0).toLocaleString("es-MX")} MXN</p>
                </Link>
              ))}
            </div>
          </div>

          <aside className="lg:sticky lg:top-4 self-start">
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Precio del paquete</p>
              <p className="mt-1 text-4xl font-extrabold text-[#0B0B0B] tabular-nums" style={{ letterSpacing: "-0.02em" }}>
                ${b.bundle_price_mxn.toLocaleString("es-MX")}
                <span className="ml-1 text-xs font-semibold text-gray-500 uppercase tracking-widest">MXN</span>
              </p>
              <p className="mt-1 text-sm text-gray-600 line-through">${b.individual_total_mxn.toLocaleString("es-MX")}</p>
              <div className="mt-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-800 border border-emerald-500/30 text-xs font-bold uppercase tracking-wider" data-testid="bundle-savings">
                Ahorras ${b.savings_mxn.toLocaleString("es-MX")} · {b.savings_pct}%
              </div>
              {b.iada_aval_available && (
                <p className="mt-3 text-xs text-[#F26A21] font-semibold inline-flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Aval IADA disponible
                </p>
              )}
              <button onClick={handleAdd} disabled={inCart}
                      data-testid="bundle-add-to-cart"
                      className={`mt-4 w-full inline-flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-bold ${inCart ? "bg-emerald-500/20 text-emerald-800 border border-emerald-500/40" : "bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B]"}`}>
                {inCart ? <><Check className="w-4 h-4" /> Ya en carrito</> : <><ShoppingCart className="w-4 h-4" /> Agregar al carrito</>}
              </button>
              <p className="mt-2 text-[10px] text-gray-500 text-center">
                Al comprar el paquete quedas inscrito en las {b.programs.length} cohortes públicas.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </PublicLayout>
  );
}
